import { Injectable } from '@angular/core';

export interface RfaAccessTokenData {
  accessToken: string;
  applicationNumber: string;
  expiresAt: string;
}

const RFA_ACCESS_TOKEN_STORAGE_KEY = 'irembo.rfaAccessToken';

@Injectable({ providedIn: 'root' })
export class RfaAccessTokenService {
  private rfaAccessToken: RfaAccessTokenData | null =
    this.loadRfaAccessTokenFromStorage();

  setRfaAccessToken(token: RfaAccessTokenData): void {
    this.rfaAccessToken = token;
    try {
      sessionStorage.setItem(
        RFA_ACCESS_TOKEN_STORAGE_KEY,
        JSON.stringify(token)
      );
    } catch {
      // Keep the in-memory token when sessionStorage is unavailable.
    }
  }

  getRfaAccessToken(): string | null {
    if (!this.rfaAccessToken) {
      return null;
    }
    const expiresAt = Date.parse(this.rfaAccessToken.expiresAt);
    if (Number.isNaN(expiresAt) || expiresAt <= Date.now()) {
      this.clearRfaAccessToken();
      return null;
    }
    return this.rfaAccessToken.accessToken;
  }

  clearRfaAccessToken(): void {
    this.rfaAccessToken = null;
    sessionStorage.removeItem(RFA_ACCESS_TOKEN_STORAGE_KEY);
  }

  private loadRfaAccessTokenFromStorage(): RfaAccessTokenData | null {
    try {
      const raw = sessionStorage.getItem(RFA_ACCESS_TOKEN_STORAGE_KEY);
      if (!raw) {
        return null;
      }

      const token = JSON.parse(raw) as Partial<RfaAccessTokenData>;
      const expiresAt = Date.parse(token.expiresAt ?? '');
      if (
        !token.accessToken ||
        !token.applicationNumber ||
        Number.isNaN(expiresAt) ||
        expiresAt <= Date.now()
      ) {
        sessionStorage.removeItem(RFA_ACCESS_TOKEN_STORAGE_KEY);
        return null;
      }

      return token as RfaAccessTokenData;
    } catch {
      sessionStorage.removeItem(RFA_ACCESS_TOKEN_STORAGE_KEY);
      return null;
    }
  }
}
